import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useContext,
} from "react";
import PropTypes from "prop-types";

import { useBreakpoint } from "./WidgetBreakpoint";
import { WidgetContext } from "./WidgetContext";

import {
  XYPlot,
  XAxis,
  YAxis,
  LineSeries,
  ChartLabel,
  CustomSVGSeries,
  makeWidthFlexible,
} from "react-vis";
import "react-vis/dist/style.css";

const FlexibleXYPlot = makeWidthFlexible(XYPlot);
import { Colours } from "@learningblocks/shared-components";

export default function LineChart(props) {
  const widget = useContext(WidgetContext);
  const breakpoints = useBreakpoint();

  // Hover states
  const [hoverValue1, setHoverValue1] = useState(null);
  useEffect(() => {
    // Reset the hover state
    setHoverValue1(null);
  }, [
    widget.countPlaying,
    widget.cycleCounter,
    widget.cycleCounterStop,
    widget.efficiencyCounter,
  ]);

  const [hoverValue2, setHoverValue2] = useState(null);
  useEffect(() => {
    // Reset the hover state
    setHoverValue2(null);
  }, [
    widget.countPlaying,
    widget.cycleCounter,
    widget.cycleCounterStop,
    widget.efficiencyCounter,
  ]);

  const [hoverValue3, setHoverValue3] = useState(null);
  useEffect(() => {
    // Reset the hover state
    setHoverValue3(null);
  }, [
    widget.countPlaying,
    widget.cycleCounter,
    widget.cycleCounterStop,
    widget.efficiencyCounter,
  ]);

  const targetRef = useRef();
  const [chartDimensions, setChartDimensions] = useState({});
  useLayoutEffect(() => {
    const updateChart = () =>
      setChartDimensions(targetRef.current.getBoundingClientRect());

    // Define the data on component load
    updateChart();

    // set resize listener
    window.addEventListener("resize", updateChart);

    // clean up function
    return () => {
      // remove resize listener
      window.removeEventListener("resize", updateChart);
    };
  }, [targetRef.current, props.visible]);

  const axisStyle = {
    ticks: {
      fontSize: "14px",
      fill: "#FFF",
    },
    title: {
      fontSize: "16px",
      fill: "#FFF",
    },
  };

  const maxOfArrX = (arr) => {
    if (arr.length > 0) {
      return arr.reduce(function (p, v) {
        return p.x > v.x ? p.x : v.x;
      });
    } else return arr;
  };

  const maxOfArrY = (arr) => {
    if (arr.length > 0) {
      return arr.reduce(function (p, v) {
        return p.y > v.y ? p.y : v.y;
      });
    } else return arr;
  };

  const numberWithCommas = (x) => {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getLogYTicks = (totalData) => {
    let data;

    if (totalData.length > 6) {
      data = totalData
        .filter((item, idy) => {
          if (idy % Math.floor(totalData.length / 6) === 0) {
            return item.y;
          }
        })
        .map((item) => item.y);
    } else {
      data = totalData.map((item) => item.y);
    }

    return data;
  };

  const getXTicks = (totalData) => {
    let data;

    if (props.cycleCounter !== 1 && props.cycleCounter > 14) {
      data = totalData
        .filter((item, idx) => {
          if (idx % 2) {
            return item.x;
          }
        })
        .map((item) => item.x);
      data.unshift(0);
      return data;
    } else {
      data = totalData.map((item) => item.x);
      data.unshift(0);
      return data.slice(0, props.cycleCounter + 1);
    }
  };

  // const getXTicks = (totalData) => {
  //   let data = totalData.map((item) => item.x);
  //
  //   if(props.cycleCounter === 1){
  //     return null
  //   } else {
  //     return data.slice(0, (props.cycleCounter + 1));
  //   }
  // };

  return (
    <div
      className="line-chart"
      ref={targetRef}
      style={{ width: "100%", height: "auto" }}
    >
      <div
        className="chart-legend"
        style={{
          display: widget.showTooltips ? "inline-block" : "none",
          marginLeft: !breakpoints.sm ? "130px" : "0",
          background: "#002F52",
          borderRadius: "5px",
          padding: "4px",
        }}
      >
        <div style={{}}>
          <p style={{ color: "white", margin: 0 }}>
            {"Cycle: " + (hoverValue1 ? numberWithCommas(hoverValue1.x) : null)}
          </p>
          <p style={{ color: Colours.yellow1, margin: 0 }}>
            {"Total DNA: " +
              (hoverValue1 ? numberWithCommas(hoverValue1.y) : null)}
          </p>
          <p style={{ color: Colours.red1, margin: 0 }}>
            {"Desired region: " +
              (hoverValue2 ? numberWithCommas(hoverValue2.y) : null)}
          </p>
          <p style={{ color: Colours.blue2, margin: 0 }}>
            {"Overhang products: " +
              (hoverValue3 ? numberWithCommas(hoverValue3.y) : null)}
          </p>
        </div>
      </div>
      <FlexibleXYPlot
        //width={props.width}
        height={props.height}
        yType={props.type}
        margin={{ left: 130, top: 20, bottom: 60 }}
        // To Do: Document these magic numbers
        xDomain={[
          0,
          maxOfArrX(props.totalData) < 30 && props.fluidXAxis
            ? maxOfArrX(props.totalData)
            : 30,
        ]}
        yDomain={[
          props.type === "log" ? 1 : 0,
          !props.fluidYAxis ? 1073741824 : maxOfArrY(props.totalData),
        ]}
      >
        {/*getHint()*/}
        <ChartLabel
          text={props.xAxisLabel}
          includeMargin={false}
          xPercent={0.45}
          yPercent={1.33}
          style={{
            fill: "white",
            fontSize: "20px",
          }}
        />

        <ChartLabel
          text={props.yAxisLabel}
          includeMargin={true}
          xPercent={13 / chartDimensions.width}
          //yPercent={0}
          style={{
            fill: "white",
            fontSize: "20px",
            transform: "rotate(-90)",
            textAnchor: "middle",
          }}
        />

        <XAxis
          bottom={0}
          tickFormat={(v) => v.toFixed(0)}
          tickValues={props.fluidXAxis ? getXTicks(props.totalData) : null}
          style={axisStyle}
        />
        <YAxis
          left={0}
          style={axisStyle}
          tickValues={
            props.fluidYAxis && props.type === "log"
              ? getLogYTicks(props.totalData)
              : null
          }
          tickFormat={(tick) => numberWithCommas(tick.toFixed())}
        />
        <LineSeries
          getNull={(d) => d.x !== null && d.y !== null}
          color={Colours.yellow1}
          opacity={props.line1Display ? 1 : 0}
          data={props.totalData.filter((data) => {
            if (props.type === "log") {
              return data.y > 0 && data.x > 0;
            } else {
              return data;
            }
          })}
        />
        {props.line1Display ? (
          <CustomSVGSeries
            getNull={(d) => d.x !== null && d.y !== null}
            customComponent="circle"
            strokeWidth="3"
            onNearestXY={(value, { index }) => {
              setHoverValue1(props.totalData[index], index);
              setHoverValue2(props.desiredData[index], index);
              setHoverValue3(props.overhangData[index], index);
            }}
            data={props.totalData.map((row) => {
              return {
                ...row,
                ...{
                  size: 10,
                  style: {
                    fill: Colours.yellow1,
                    stroke: "white",
                    strokeWidth:
                      hoverValue1 !== null && hoverValue1.x === row.x
                        ? "3px"
                        : 0,
                    strokeLinejoin: "round",
                  },
                },
              };
            })}
          />
        ) : null}
        <LineSeries
          getNull={(d) => d.x !== null && d.y !== null}
          color={Colours.red1}
          opacity={props.line2Display ? 1 : 0}
          data={props.desiredData.filter((data) => {
            if (props.type === "log") {
              return data.y > 0 && data.x > 0;
            } else {
              return data;
            }
          })}
        />
        {props.line2Display ? (
          <CustomSVGSeries
            getNull={(d) => d.x !== null && d.y !== null}
            customComponent="square"
            strokeWidth="3"
            onNearestXY={(value, { index }) => {
              setHoverValue1(props.totalData[index], index);
              setHoverValue2(props.desiredData[index], index);
              setHoverValue3(props.overhangData[index], index);
            }}
            data={props.desiredData
              .filter((data) => {
                if (props.type === "log") {
                  return data.y > 0 && data.x > 0;
                } else {
                  return data;
                }
              })
              .map((row) => {
                return {
                  ...row,
                  ...{
                    size: 10,
                    style: {
                      fill: Colours.red1,
                      stroke: "white",
                      strokeWidth:
                        hoverValue1 !== null && hoverValue1.x === row.x
                          ? "3px"
                          : 0,
                      strokeLinejoin: "round",
                    },
                  },
                };
              })}
          />
        ) : null}
        <LineSeries
          getNull={(d) => d.x !== null && d.y !== null}
          color={Colours.blue2}
          opacity={props.line3Display ? 1 : 0}
          data={props.overhangData.filter((data) => {
            if (props.type === "log") {
              return data.y > 0 && data.x > 0;
            } else {
              return data;
            }
          })}
        />
        {props.line3Display ? (
          <CustomSVGSeries
            getNull={(d) => d.x !== null && d.y !== null}
            customComponent="diamond"
            stroke="3"
            onNearestXY={(value, { index }) => {
              setHoverValue1(props.totalData[index], index);
              setHoverValue2(props.desiredData[index], index);
              setHoverValue3(props.overhangData[index], index);
            }}
            data={props.overhangData
              .filter((data) => {
                if (props.type === "log") {
                  return data.y > 0 && data.x > 0;
                } else {
                  return data;
                }
              })
              .map((row) => {
                return {
                  ...row,
                  ...{
                    size: 12,
                    style: {
                      fill: Colours.blue2,
                      stroke: "white",
                      transform: "translateY(-6px)",
                      strokeWidth:
                        hoverValue1 !== null && hoverValue1.x === row.x
                          ? "3px"
                          : 0,
                      strokeLinejoin: "round",
                    },
                  },
                };
              })}
          />
        ) : null}
      </FlexibleXYPlot>
    </div>
  );
}

LineChart.propTypes = {
  width: PropTypes.int,
  height: PropTypes.int,
  type: PropTypes.string,
  fluidXAxis: PropTypes.boolean,
  fluidYAxis: PropTypes.boolean,
  totalData: PropTypes.object,
  desiredData: PropTypes.object,
  overhangData: PropTypes.object,
  line1Display: PropTypes.boolean,
  line2Display: PropTypes.boolean,
  cycleCounter: PropTypes.int,
  visible: PropTypes.boolean,
};
